import { captureException } from '@sentry/react';
import { cloneDeep, omit } from 'lodash';

import createCollectionResource, { AddOptions, UpdateOptions } from 'src/actions/shared/createResource';
import { DomainError } from 'src/models/DomainError';
import { getSchema } from 'src/actions/integrations/validation/schemas';
import {
  FIELD_LABEL_DELIMITER,
  addSecrets,
  processEmptySecrets,
  translateJoiErrors,
} from 'src/actions/shared/validationHelpers';
import { ExternalSource } from './integrationSources';
import { toastError } from 'src/actions/toast';
export type { ExternalSource };
export type { AutoLinkField, ExternalSourceType } from './commonTypes';
export type { ExternalConnectionSettings, ExternalSourceSecrets, SearchField } from './integrationSources';
export { AutoLinkFields, ExternalSourceTypes } from './commonTypes';

/**
 * These are the "read only" attributes that we receive from the backend and should remove
 * before posting the response back
 */
export const TRANSIENT_ATTRIBUTES = ['secrets.hasSecrets', 'secrets.hasCustomHeaders', 'createdAt', 'updatedAt'];

const integrationsResource = createCollectionResource<ExternalSource>({
  name: 'integrations',
  endpoint: 'v1/orgs/:orgId/integrations/:id',
  options: {
    patchOnUpdate: true,
    toastMessages: {
      onAdd: item => `Added "${item.name}"`,
      onUpdate: item => `Updated "${item.name}"`,
    },
  },
});
const { add, update } = integrationsResource.effects;

const removeTransientAttrs = <T extends object>(source: T): Partial<T> => {
  return omit(cloneDeep(source), TRANSIENT_ATTRIBUTES);
};

integrationsResource.effects.addWithTransform = <T extends Partial<ExternalSource>>(
  params: unknown,
  attrs: T,
  opts: AddOptions
) => {
  const clean = removeTransientAttrs(processEmptySecrets(attrs));
  return add(params, clean, opts);
};
integrationsResource.effects.updateWithTransform = <T extends Partial<ExternalSource>>(
  params: unknown,
  attrs: T,
  opts?: UpdateOptions
) => {
  const clean = removeTransientAttrs(processEmptySecrets(attrs));
  return update(params, clean, opts);
};

/**
 * This validator may be invoked as a part of a chain so it takes into account the
 * previously discovered errors and tries to display a combined list
 *
 * @param item - data object to validate
 * @param knownErrors - errors that were found by other validators (if any)
 */
integrationsResource.effects.validateItem =
  <T extends ExternalSource>(item: T, knownErrors?: DomainError[]) =>
  dispatch => {
    const schema = getSchema(item.type);
    if (!schema) return true; // We don't know how to validate this, delegate to the backend
    const withSecrets = removeTransientAttrs(addSecrets(item));
    const toastMessage = 'Invalid entry. Please fix the errors and try again';
    const exceptionToast = 'Unable to save. Please verify all fields and try again';
    try {
      const combinedErrors = knownErrors ? [...knownErrors] : [];
      const { error } = schema.validate(withSecrets, {
        abortEarly: false,
        allowUnknown: true,
        skipFunctions: true,
        errors: {
          label: false,
          wrap: {
            label: FIELD_LABEL_DELIMITER,
          },
        },
      });

      if (error) {
        const newErrors = translateJoiErrors(error);
        combinedErrors.push(...newErrors);
      }

      if (combinedErrors.length) {
        dispatch(integrationsResource.actions.validationError(combinedErrors));
        dispatch(toastError(toastMessage));
        return false;
      } else {
        return true;
      }
    } catch (err) {
      // If we are here, it is likely a bug in Joi. Log a sentry
      captureException(err, { tags: { originalItem: JSON.stringify(item, null, 2) } });
      dispatch(toastError(exceptionToast));
      return false;
    }
  };

export default integrationsResource;
